import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useQuotationInvoiceDocumentsQuery, useQuotationInvoiceSummaryQuery } from "../api/queries";
import { DocumentsFilter } from "../ui/DocumentsFilter";
import { DocumentsTable } from "../ui/DocumentsTable";
import { DocumentsToolbar } from "../ui/DocumentsToolbar";
import {
  DocumentsCountSummarySection,
  DocumentsCountSummarySectionSkeleton,
} from "../ui/DocumentsCountSummarySection";
import { SummaryCards } from "../ui/SummaryCards";
import { DocumentsTour, useDocumentsTour } from "../tour/DocumentsTour";
import { computeDocumentsCountSummary } from "../lib/documents-count-summary";

const DEFAULT_DOCUMENTS_SORT = [{ field: "createdAt", dir: "desc" as const }];

export function QuotationInvoiceDocumentsPage() {
  const nav = useNavigate({ from: "/app/documents" });
  const search = useSearch({ from: "/app/documents" }) as OffsetPageQueryDTO & {
    documentType?: string;
  };
  const tour = useDocumentsTour();
  const sort = search.sort ?? DEFAULT_DOCUMENTS_SORT;

  const listQuery = useQuotationInvoiceDocumentsQuery({
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sort,
    filters: search.filters,
  });

  const summaryQuery = useQuotationInvoiceSummaryQuery();
  const countSummary = computeDocumentsCountSummary(summaryQuery.data);
  const canCreate = useActionPermission(["quotation_invoice:create"]);
  const canUpdate = useActionPermission(["quotation_invoice:update"]);

  return (
    <>
      <Header />

      <Main>
        <DocumentsTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <DocumentsToolbar
          canCreate={!!canCreate}
          onCreate={() => nav({ to: "/app/documents/new" })}
          onStartTour={tour.startTour}
          canManageIssuerSettings={!!canUpdate}
          onIssuerSettings={() =>
            nav({ to: "/app/documents/settings" })
          }
        />

        <div className="mb-4 space-y-4" data-tourid="documents-summary">
          {summaryQuery.isLoading ? (
            <DocumentsCountSummarySectionSkeleton />
          ) : (
            <DocumentsCountSummarySection summary={countSummary} />
          )}
          <SummaryCards
            data={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card p-4">
          <DocumentsFilter
            searchParams={search}
            fromRoute="/app/documents"
          />

          <div data-tourid="documents-table">
            <DocumentsTable
            data={listQuery.data?.data ?? []}
            isLoading={listQuery.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={listQuery.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({
                search: {
                  offset,
                  limit,
                  sort,
                  filters: search.filters,
                },
              })
            }
            sortBy={sort[0]?.field}
            sortOrder={sort[0]?.dir as "asc" | "desc" | undefined}
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  offset: search.offset ?? 0,
                  limit: search.limit ?? 20,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  filters: search.filters,
                },
              })
            }
            onView={(row) => nav({ to: "/app/documents/$id", params: { id: row.id } })}
            onEdit={(row) =>
              nav({ to: "/app/documents/$id/edit", params: { id: row.id } })
            }
          />
          </div>
        </div>
      </Main>
    </>
  );
}
